// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import axios from '../utils/axiosConfig';
import { isMaintenance } from '../utils/maintenanceMode';

const ContainerPullContext = createContext(null);

// ─── Constants ────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 7200; // ~6 hours — large images over cellular take a long time
const MAX_404_RETRIES = 2;

// Terminal statuses — once reached, a stargate NEVER moves back.
const TERMINAL = new Set(['completed', 'failed']);

// Persisted set of dismissed job IDs. The backend keeps a job file for ~5 min
// after completion (see list_pull_jobs), so without this guard a dismissed job
// could be re-fetched by fetchActiveJobs on the next reload/remount and
// re-populate a row the user explicitly cleared. We remember dismissed IDs
// (with a timestamp so the list stays bounded) and skip them on discovery.
const DISMISSED_KEY = 'containerPull.dismissedJobs';
const DISMISSED_TTL_MS = 24 * 60 * 60 * 1000; // prune entries older than 24h

const loadDismissed = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '{}');
    const now = Date.now();
    const pruned = {};
    for (const [id, ts] of Object.entries(raw)) {
      if (typeof ts === 'number' && now - ts < DISMISSED_TTL_MS) pruned[id] = ts;
    }
    return pruned;
  } catch {
    return {};
  }
};

const persistDismissed = (map) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
  } catch {
    /* localStorage unavailable (private mode / quota) — in-memory guard still applies */
  }
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ContainerPullProvider = ({ children }) => {
  const [jobs, setJobs] = useState({});
  const pollTimersRef = useRef({});
  const mountedRef = useRef(true);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  // IDs the user has explicitly dismissed — never re-add these on discovery.
  const dismissedRef = useRef(loadDismissed());

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Core state updater: the ONLY way stargate status changes ──
  // Guarantees: terminal states never regress, single atomic update per poll.
  const applyBackendData = useCallback((jobId, backendJob) => {
    if (!mountedRef.current) return;
    setJobs((prev) => {
      const existing = prev[jobId];
      if (!existing) return prev;

      // Merge each stargate's status with terminal-state protection
      const mergedStargates = {};
      for (const [ip, backendSg] of Object.entries(backendJob.stargates || {})) {
        const current = existing.stargates[ip];
        // RULE: Once terminal, NEVER change.
        if (current && TERMINAL.has(current.status)) {
          mergedStargates[ip] = current;
        } else {
          mergedStargates[ip] = {
            ...current,
            ...backendSg,
            // Preserve name from initial setup
            name: current?.name || backendSg.name || ip,
          };
        }
      }
      // Also preserve any stargates we know about that backend didn't mention
      for (const [ip, current] of Object.entries(existing.stargates)) {
        if (!mergedStargates[ip]) {
          mergedStargates[ip] = current;
        }
      }

      // Compute overall job status from merged stargate statuses
      const allStatuses = Object.values(mergedStargates).map((s) => s.status);
      const allTerminal = allStatuses.every((s) => TERMINAL.has(s));
      const successCount = allStatuses.filter((s) => s === 'completed').length;
      const failCount = allStatuses.filter((s) => s === 'failed').length;

      let jobStatus = 'in_progress';
      if (allTerminal) {
        if (failCount === 0) jobStatus = 'completed';
        else if (successCount === 0) jobStatus = 'failed';
        else jobStatus = 'partial';
      }

      return {
        ...prev,
        [jobId]: {
          ...existing,
          status: jobStatus,
          stargates: mergedStargates,
          completedAt: allTerminal ? existing.completedAt || Date.now() : undefined,
        },
      };
    });
  }, []);

  // ── Polling ──
  const startPolling = useCallback(
    (jobId) => {
      if (pollTimersRef.current[jobId]) return;

      let attempts = 0;
      let notFoundCount = 0;

      const poll = async () => {
        // If the user has logged out (token cleared — e.g. inactivity timeout),
        // stop polling immediately. Otherwise every tick fires an unauthenticated
        // request that the browser logs as a 401, flooding the console. Discovery
        // re-runs on the next login to resume any still-active jobs.
        if (!localStorage.getItem('token')) {
          clearInterval(pollTimersRef.current[jobId]);
          delete pollTimersRef.current[jobId];
          return;
        }
        attempts++;
        try {
          const { data } = await axios.get(`/registry/pull-status/${jobId}`);
          if (!mountedRef.current) return;

          applyBackendData(jobId, data);
          notFoundCount = 0; // reset on success

          // Check if all done (read from latest state)
          const allDone = Object.values(data.stargates || {}).every((s) => TERMINAL.has(s.status));
          // Also check our local terminal overrides
          setJobs((current) => {
            const job = current[jobId];
            if (job) {
              const localAllDone = Object.values(job.stargates || {}).every((s) =>
                TERMINAL.has(s.status)
              );
              if (localAllDone && pollTimersRef.current[jobId]) {
                clearInterval(pollTimersRef.current[jobId]);
                delete pollTimersRef.current[jobId];
              }
            }
            return current; // no mutation
          });

          if (allDone) {
            clearInterval(pollTimersRef.current[jobId]);
            delete pollTimersRef.current[jobId];
          }
        } catch (err) {
          if (err.response?.status === 404) {
            notFoundCount++;
            if (notFoundCount > MAX_404_RETRIES) {
              clearInterval(pollTimersRef.current[jobId]);
              delete pollTimersRef.current[jobId];
              setJobs((prev) => {
                const updated = { ...prev };
                delete updated[jobId];
                return updated;
              });
            }
          }
        }

        if (attempts >= MAX_POLL_ATTEMPTS) {
          // Frontend poll limit reached but the backend keeps running.
          // Just stop polling — don't mark the job as "timeout" because the
          // next page visit will resume polling and pick up the real status.
          clearInterval(pollTimersRef.current[jobId]);
          delete pollTimersRef.current[jobId];
        }
      };

      // First poll after a short delay, then interval
      const timerId = setInterval(poll, POLL_INTERVAL_MS);
      pollTimersRef.current[jobId] = timerId;
      setTimeout(poll, 800); // immediate first poll
    },
    [applyBackendData]
  );

  // ── Stop every active poll timer (used on logout) ──
  const stopAllPolling = useCallback(() => {
    for (const jobId of Object.keys(pollTimersRef.current)) {
      clearInterval(pollTimersRef.current[jobId]);
      delete pollTimersRef.current[jobId];
    }
  }, []);

  // ── Discover active jobs from backend ──
  const fetchActiveJobs = useCallback(async () => {
    if (isMaintenance()) return;
    // Don't poll before the user is authenticated (e.g. on the login page) —
    // otherwise this fires a 401 on every fresh load.
    if (!localStorage.getItem('token')) return;
    try {
      const { data } = await axios.get('/registry/pull-jobs');
      if (!mountedRef.current) return;
      if (data && Object.keys(data).length > 0) {
        setJobs((prev) => {
          const merged = { ...prev };
          for (const [jobId, jobData] of Object.entries(data)) {
            // Skip jobs the user has explicitly dismissed/cleared.
            if (dismissedRef.current[jobId]) continue;
            if (!merged[jobId]) {
              merged[jobId] = {
                jobId,
                image: jobData.image,
                status: jobData.status || 'in_progress',
                // NOTE: backend created_at is a MONOTONIC clock (asyncio loop time),
                // not epoch — treating it as wall-clock produced absurd "elapsed"
                // values (e.g. "29706978m"). Anchor to first-seen instead. Set once
                // (this branch only runs for jobs not already tracked), so elapsed
                // grows correctly across polls.
                startedAt: Date.now(),
                // AB#920: started before this view saw it — first-seen is NOT the real
                // start, so the UI must not show "took 0s" for it.
                discovered: true,
                stargates: {},
              };
              for (const [ip, sg] of Object.entries(jobData.stargates || {})) {
                merged[jobId].stargates[ip] = {
                  status: sg.status || 'pending',
                  progress: sg.progress || 0,
                  message: sg.message || '',
                  name: sg.name || ip,
                };
              }
            }
          }
          return merged;
        });
        for (const jobId of Object.keys(data)) {
          if (dismissedRef.current[jobId]) continue;
          startPolling(jobId);
        }
      }
    } catch (err) {
      // 401/404 are expected during auth gaps / when the endpoint is briefly
      // unavailable — don't spam the console for those.
      if (err.response?.status !== 401 && err.response?.status !== 404) {
        console.warn('[ContainerPull] Failed to fetch active jobs from backend:', err.message);
      }
    }
  }, [startPolling]);

  // ── Run discovery on mount, and react to auth changes ──
  // On logout (inactivity timeout or explicit) we must stop every poll timer —
  // otherwise they keep firing unauthenticated requests that the browser logs as
  // 401s, flooding the console. On the next login we re-discover so any job still
  // running server-side resumes in the UI.
  useEffect(() => {
    fetchActiveJobs();

    const handleLogout = () => stopAllPolling();
    const handleLogin = () => {
      fetchActiveJobs();
    };

    window.addEventListener('user-logout', handleLogout);
    window.addEventListener('login', handleLogin);
    return () => {
      window.removeEventListener('user-logout', handleLogout);
      window.removeEventListener('login', handleLogin);
    };
  }, [fetchActiveJobs, stopAllPolling]);

  // ── Actions ──

  const initiatePull = useCallback(
    async ({ image, stargateIps, stargateNames }) => {
      try {
        const { data } = await axios.post('/registry/pull-on-stargate', {
          image,
          stargate_ips: stargateIps,
        });

        const jobId = data.pull_job_id;
        if (!jobId) {
          return {
            success: true,
            jobId: null,
            message: 'Pull started but no job ID returned.',
          };
        }

        const stargates = {};
        stargateIps.forEach((ip, idx) => {
          stargates[ip] = {
            status: 'pending',
            progress: 0,
            message: 'Waiting to start...',
            name: stargateNames?.[idx] || ip,
          };
        });

        setJobs((prev) => ({
          ...prev,
          [jobId]: {
            jobId,
            image,
            status: 'in_progress',
            stargates,
            startedAt: Date.now(),
          },
        }));

        startPolling(jobId);
        return { success: true, jobId };
      } catch (err) {
        return {
          success: false,
          jobId: null,
          error: err.response?.data?.detail || err.message || 'Failed to start pull',
        };
      }
    },
    [startPolling]
  );

  const dismissJob = useCallback((jobId) => {
    if (pollTimersRef.current[jobId]) {
      clearInterval(pollTimersRef.current[jobId]);
      delete pollTimersRef.current[jobId];
    }
    // Remember the dismissal so a still-cached backend job can't re-populate
    // this row on the next reload/remount, and persist it across reloads.
    dismissedRef.current = { ...dismissedRef.current, [jobId]: Date.now() };
    persistDismissed(dismissedRef.current);
    setJobs((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    // Authoritatively clear the job on the backend (idempotent). Best-effort:
    // the local dismissed-guard already prevents re-population if this fails.
    axios.delete(`/registry/pull-jobs/${jobId}`).catch(() => {
      /* non-fatal */
    });
  }, []);

  const clearFinishedJobs = useCallback(() => {
    setJobs((prev) => {
      const next = {};
      for (const [id, job] of Object.entries(prev)) {
        const anyActive = Object.values(job.stargates || {}).some((s) => !TERMINAL.has(s.status));
        if (anyActive) {
          next[id] = job;
        } else {
          if (pollTimersRef.current[id]) {
            clearInterval(pollTimersRef.current[id]);
            delete pollTimersRef.current[id];
          }
          // Make the clear authoritative so finished jobs can't re-populate.
          dismissedRef.current = { ...dismissedRef.current, [id]: Date.now() };
          axios.delete(`/registry/pull-jobs/${id}`).catch(() => {
            /* non-fatal */
          });
        }
      }
      return next;
    });
    persistDismissed(dismissedRef.current);
  }, []);

  // ── Derived values ──

  const hasActiveJobs = useMemo(
    () =>
      Object.values(jobs).some((job) =>
        Object.values(job.stargates || {}).some((s) => !TERMINAL.has(s.status))
      ),
    [jobs]
  );

  const jobsList = useMemo(
    () => Object.values(jobs).sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0)),
    [jobs]
  );

  const value = useMemo(
    () => ({
      jobs,
      jobsList,
      hasActiveJobs,
      initiatePull,
      dismissJob,
      clearFinishedJobs,
    }),
    [jobs, jobsList, hasActiveJobs, initiatePull, dismissJob, clearFinishedJobs]
  );

  return <ContainerPullContext.Provider value={value}>{children}</ContainerPullContext.Provider>;
};

// ── Hook & export ─────────────────────────────────────────────────────────────

export const useContainerPull = () => {
  const ctx = useContext(ContainerPullContext);
  if (!ctx) throw new Error('useContainerPull must be used within ContainerPullProvider');
  return ctx;
};
